import { Button, Row, Select, Space, Typography, Tag, Alert } from "antd";
import React from "react";
import { useState } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import "./styles.css";
import { useEffect } from "react";
import { TrainingAPI } from "../../api/TrainingAPI";
import { formattedPlayerName } from "../../Utils/name_utils";
import { useSelector, useDispatch } from "react-redux";
import { getTeamPlayers } from "../../store/actions/team.actions";
import { audioMessage as message } from "../Common/Notifications/audioMessage";
import { 
  PermissionedButton, 
  PermissionedSelect 
} from "../PermissionedComponents";
import PermissionViewable from "../PermissionedComponents/PermissionViewable";
import PermissionOverlay from "../PermissionedComponents/PermissionOverlay";

const { Title } = Typography;

const sortByOptions = [
  { value: "first_name", label: "First Name" },
  { value: "last_name", label: "Last Name" },
  { value: "number", label: "Number" },
  { value: "position", label: "Position" },
];
const sortOptions = [
  { value: "asc", label: "Ascending" },
  { value: "desc", label: "Descending" },
];

const tag_colors = [
  "processing",
  "success",
  "error",
  "warning",
  "magenta",
  "red",
  "volcano",
  "orange",
  "gold",
  "lime",
  "green",
  "cyan",
  "blue",
  "geekblue",
  "purple",
];

const trainingAPI = new TrainingAPI();

export const PlayerOrdering = () => {
  const dispatch = useDispatch();
  const {
    playerGroups,
    players,
    info: { display_name: teamName },
  } = useSelector(({ team }) => team);
  const [itemList, setItemList] = useState(players);
  const [sort, setSort] = useState("asc");
  const [sortBy, setSortBy] = useState(null);
  const [unsavedChanges, setUnsavedChanges] = useState(false)

  useEffect(() => {
    setItemList(players);
  }, [players]);

  useEffect(() => {
    if (JSON.stringify(itemList) !== JSON.stringify(players) || itemList.every((e) => e?.custom_order === 0)) {
      setUnsavedChanges(true)
    } else {
      setUnsavedChanges(false)
    }
  }, [itemList])

  useEffect(() => {
    const sorted = [...itemList].sort((a, b) =>
      a[sortBy] < b[sortBy]
        ? sort === "asc"
          ? -1
          : 1
        : sort === "asc"
        ? 1
        : -1
    );
    setItemList(sorted);
  }, [sort, sortBy]);

  // Function to update list on drop
  const handleDrop = (droppedItem) => {
    // Ignore drop outside droppable container
    if (!droppedItem.destination) return;
    var updatedList = [...itemList];
    // Remove dragged item
    const [reorderedItem] = updatedList.splice(droppedItem.source.index, 1);
    // Add dropped item
    updatedList.splice(droppedItem.destination.index, 0, reorderedItem);
    // Update State
    setItemList(updatedList);
  };

  const handleSortChange = (val) => {
    setSort(val);
  };

  const handleSortByChange = (val) => {
    setSortBy(val);
  };

  const handleSave = () => {
    trainingAPI
      .savePlayerOrdering(
        itemList.reduce(
          (cur, item, index) => ({ ...cur, [item.player_id]: index + 1 }),
          {}
        )
      )
      .then((data) => {
        message.success("Player order saved successfully");
        dispatch(getTeamPlayers());
      })
      .catch((err) => message.error("Error saving player order"));
  };

  return (
    <div style={{ display: "flex" }}>
      <div style={{ width: 630, maxWidth: "100%", margin: "24px 0px" }}>
        <div
          style={{
            marginBottom: 8,
            display: "flex",
            alignItems: "center",
            gap: 16,
          }}
        >
          <Select
              options={sortByOptions}
              value={sortBy}
              showSearch
              optionFilterProp="value"
              onSelect={handleSortByChange}
              placeholder="Select Sort By"
            />
            <Select
              options={sortOptions}
              value={sort}
              showSearch
              optionFilterProp="value"
              onSelect={handleSortChange}
            />
            <PermissionedButton
              permissionCheck="EditPlayerOrdering"
              type="primary"
              onClick={handleSave}
              className={unsavedChanges && 'pulse'}
              disabled={unsavedChanges === false}
            >
              Save Order
            </PermissionedButton>
          {unsavedChanges && 
          <Alert
            message="You have unsaved changes!"
            type="info"
            showIcon
          />
        }
        </div>
        <PermissionOverlay
          permissionCheck="EditPlayerOrdering"
          permissionMessage="Player ordering is restricted"
          tooltipMessage="You don't have permission to reorder players"
          hideTooltip={false}
        >
          <DragDropContext onDragEnd={handleDrop}>
            <Droppable droppableId="list-container">
              {(provided) => {
                return (
                  <div
                    className="list-container"
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                  >
                    {itemList.map((item, index) => (
                      <Draggable
                        key={item.player_id}
                        draggableId={`${item.player_id}`}
                        index={index}
                      >
                        {(provided) => (
                          <div
                            className="item-container"
                            ref={provided.innerRef}
                            {...provided.dragHandleProps}
                            {...provided.draggableProps}
                          >
                            <div className="item-element">{item.number}</div>
                            <strong className="item-element">
                              {formattedPlayerName(
                                teamName,
                                item.first_name + " " + item.last_name
                              )}
                            </strong>
                            <div className="item-element">{item.position}</div>
                            <div className="item-element">
                              {item?.player_groups?.map((player_group) => {
                                let pg_item = playerGroups.find(
                                  (x) => x.player_group_id == player_group
                                );
                                return pg_item ? (
                                  <Tag
                                    color={
                                      tag_colors[
                                        Math.min(
                                          playerGroups.indexOf(pg_item),
                                          tag_colors.length - 1
                                        )
                                      ]
                                    }
                                  >
                                    {pg_item.name}
                                  </Tag>
                                ) : null;
                              }) || <></>}
                            </div>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                );
              }}
            </Droppable>
          </DragDropContext>
        </PermissionOverlay>
      </div>
    </div>
  );
};
